import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { FC, MouseEvent, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { AnimatePresence } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";

import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ActionsWrap, Td } from "@/components/Table";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import PostThumbnail from "./PostThumbnail";
import Text from "@/components/Text";
import StatusBadge from "./StatusBadge";
import { FBImageDistributionResource } from "@/types/resources";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import { ImageDistributionStatus } from "@/utils/enums";
import PostModal from "./PostModal";
import GoToLink from "@/components/GoToLink";
import ScheduledPostEditForm from "./ScheduledPostEditForm";

const Time = styled(Text)`
  margin-top: 4px;
`;

const Image = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 6px;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const ChannelHolder = styled.div`
  display: flex;
  align-items: center;
`;

const ChannelName = styled.span`
  white-space: nowrap;
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color ease-out 0.2s;
  &:hover {
    td {
      transition: background-color ease-out 0.2s;
      background-color: ${({ theme }) =>
        theme.colors.grayscaleExtraLight} !important;
    }
  }
  margin-bottom: 32px;
`;

interface Props {
  post: FBImageDistributionResource;
}

const PostsTableRow: FC<Props> = ({ post }) => {
  const [isLightboxVisible, setIsLightBoxVisible] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const handleRowClick = (id: number) => {
    navigate(`/general/image-distribution/${id}`);
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete this post?`
      );

      if (!confirm) return;

      await api.delete(`/v1/distribution/image-posts/${post.id}`);

      await queryClient.invalidateQueries(["fb_image_distribution"]);

      alerts.success("Post was successfully deleted");
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <>
      <Tr onClick={() => handleRowClick(post.id)}>
        <Td>
          <PostThumbnail
            setIsLightBoxVisible={setIsLightBoxVisible}
            thumbnail={post.image?.versions?.original}
          />
        </Td>
        <Td>{post.name}</Td>
        <Td>
          {post.postDate ? (
            <>
              {DateTime.fromISO(post.postDate).toLocaleString()} <br />
              <Time $color="gray" $type="extraSmall">
                {DateTime.fromISO(post.postDate).toFormat("HH:mm")}
              </Time>
            </>
          ) : (
            "-"
          )}
        </Td>
        <Td>
          <StatusBadge status={post.status} postDate={post.postDate} />
        </Td>
        <Td>
          {!!post.channel ? (
            <ChannelHolder>
              {post.channel.thumbnail && (
                <Image
                  src={post.channel.thumbnail}
                  alt={`${post.channel.name} thumbnail`}
                />
              )}
              <ChannelName>{post.channel.name}</ChannelName>
            </ChannelHolder>
          ) : (
            "-"
          )}
        </Td>
        <Td>
          {post.facebookLink ? (
            <GoToLink
              onClick={(e) => e.stopPropagation()}
              link={post.facebookLink}
            />
          ) : (
            "-"
          )}
        </Td>
        <Td $actionsCol>
          <ActionsWrap>
            <ActionsDropdown>
              <ActionsDropdownButton
                as="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setShowEditModal(true);
                }}
              >
                <span>
                  <PencilIcon />
                </span>
                Edit
              </ActionsDropdownButton>
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </ActionsDropdown>
          </ActionsWrap>
        </Td>
        <AnimatePresence>
          {showEditModal &&
            (post.status === ImageDistributionStatus.DRAFT ? (
              <PostModal onClose={() => setShowEditModal(false)} post={post} />
            ) : (
              <ScheduledPostEditForm
                onClose={() => setShowEditModal(false)}
                post={post}
              />
            ))}
        </AnimatePresence>
      </Tr>
      <Lightbox
        close={() => {
          setIsLightBoxVisible(false);
        }}
        render={{
          iconNext: () => <></>,
          iconPrev: () => <></>,
        }}
        styles={{
          root: {
            "--yarl__color_backdrop": "rgba(0, 0, 0, .25)",
            "--yarl__carousel_padding_px": 40,
          },
        }}
        slides={[{ src: post.image?.versions?.original }]}
        open={isLightboxVisible}
      />
    </>
  );
};

export default PostsTableRow;
